import clsx from 'clsx';
import { type FormEvent, type KeyboardEvent, type SyntheticEvent, useEffect, useRef, useState } from 'react';
import { SvgIcon } from '../../../../common/components/SvgIcon';
import { useSgwtTranslation } from '../../../../common/i18n';
import { useBotConversation } from '../../shared/BotConversationContext';

export const InputField = () => {
  const { inputText, setInputText, sendMessage } = useBotConversation();
  const { translate } = useSgwtTranslation();
  const [text, setText] = useState('');
  const [height, setHeight] = useState(48);

  useEffect(
    function updateText() {
      if (inputText !== '' && inputText !== text) {
        setText(inputText);
        setInputText('');
      }
    },
    [inputText, setInputText, text],
  );

  const handleSend = (text: string) => {
    sendMessage(text);
  };

  // This padding is used to avoid the messages panel being below the input field.
  const padding = 52 + Math.min(height, 144);

  return (
    <div className="d-flex flex-column bg-blur">
      {/* Calculate automatically the bottom padding to avoid the messages panel being below the input field. */}
      <style>{`sgwt-chatbot .sgwt-chatbot-messages { padding-bottom: ${padding}px; }`}</style>
      <ExpandableTextField
        onSend={handleSend}
        initialValue={text}
        placeholder={translate('input.placeholder')}
        onResize={(newHeight) => setHeight(newHeight)}
        size="xl"
        includeSendButton
      />

      <div className="sgwt-chatbot-footer-warning text-secondary p-0 d-flex align-items-center gap-2 mt-1" style={{ zIndex: 1 }}>
        <SvgIcon type="info" style={{ height: 16, width: 16 }} />
        <div className="d-flex flex-column fs-12 text-small">
          <span>{translate('warning.incorrect-content')}</span>
          <span>{translate('warning.no-personal-data')}</span>
        </div>
      </div>
    </div>
  );
};

const minSizes = {
  sm: 20,
  md: 32,
  lg: 40,
  xl: 48,
};

interface ExpandableTextFieldProps {
  clearOnSend?: boolean;
  disabled?: boolean;
  includeSendButton?: boolean;
  initialValue?: string;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onResize?: (height: number) => void;
  onSend: (message: string) => void;
  onValueChange?: (value: string) => void;
}

export const ExpandableTextField = ({
  clearOnSend = true,
  disabled = false,
  includeSendButton = false,
  initialValue = '',
  placeholder,
  size = 'md',
  onResize,
  onSend,
  onValueChange,
}: ExpandableTextFieldProps) => {
  const input = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState(initialValue);
  const [height, setHeight] = useState(minSizes[size]);

  useEffect(function getInitialFocus() {
    input.current?.focus();
  }, []);

  const resizeInput = (reset = false) => {
    const { current } = input;
    if (current) {
      // Calculate the new height for the input field.
      const newHeight = reset ? minSizes[size] : Math.max(minSizes[size], Math.min(current.scrollHeight, 144));
      // TODO How to _reallly_ handle the resize, especially when text / line is removed?
      // Recalculate the height of the input field based on its content. First reset the height to `auto`
      // to handle the case where a line is removed (like when pressing Backspace).
      // current.style.height = 'auto';
      // current.style.height = `${newHeight}px`;
      if (newHeight !== height) {
        onResize?.(newHeight);
        setHeight(newHeight);
      }
    }
  };

  const handleSend = (evt: FormEvent) => {
    evt.preventDefault();
    const cleanText = text.trim().trimStart().trimEnd();
    if (cleanText !== '') {
      onSend(cleanText);
      if (clearOnSend) {
        setText('');
        resizeInput(true);
      }
    }
  };

  const handleChange = (evt: SyntheticEvent<HTMLTextAreaElement>) => {
    const target = evt.target as HTMLTextAreaElement;
    setText(target.value);
    onValueChange?.(target.value);
    resizeInput();
  };

  // Handle the "Enter" key to send the message.
  const handleSending = (evt: KeyboardEvent) => {
    if (evt.key === 'Enter' && !evt.shiftKey) {
      handleSend(evt);
    }
  };

  return (
    <form onSubmit={handleSend}>
      <div className="form-group mb-0">
        {includeSendButton && (
          <div className="input-control position-absolute" style={{ position: 'absolute', bottom: '46px', right: '8px', zIndex: 1 }}>
            <button type="button" className="btn btn-discreet-primary btn-icon" disabled={!text} onClick={handleSend}>
              <i className="icon">send</i>
            </button>
          </div>
        )}
        <textarea
          ref={input}
          value={text}
          disabled={disabled}
          onKeyUp={handleSending}
          onChange={handleChange}
          className={clsx(`message-input form-control form-control-${size} scrollbar-sm`, includeSendButton && 'pe-48px')}
          rows={1}
          placeholder={placeholder}
          style={{ position: 'relative', resize: 'none', height, minHeight: minSizes[size], maxHeight: 144 }}
        />
      </div>
    </form>
  );
};
